import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import moment from 'moment';
import {
  DateRangePicker,
  DayPickerRangeController,
  FocusedInputShape,
  isInclusivelyAfterDay,
} from 'react-dates';
import 'react-dates/initialize';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import Time from '../Icons/Time';
import Arrow from '../Icons/Arrow';
import tailwindConfig from '../../../../tailwind.config';
import Button from '../Button';
import { ContentType, DISPLAY_DATE_FORMAT } from '../../../core/constants';
import Calender from '../Icons/Calender';
import analytics from '../../../core/analytics/analytics';
import SkipTranslationText from '../SkipTranslationText';
import SkipTranslationWrapper from '../SkipTranslationWrapper';

const Modal = dynamic(() => import('../Modal/Modal'));

const PrevButton = () => (
  <button type="button" className="prev-button rounded-full border border-solid p-2">
    <BsChevronLeft />
  </button>
);

const NextButton = () => (
  <button type="button" className="next-button rounded-full border border-solid p-2">
    <BsChevronRight />
  </button>
);

type CheckInAndOutDates = {
  checkInDate: moment.Moment | null;
  checkOutDate: moment.Moment | null;
};

type Props = {
  checkInAndOutDates: CheckInAndOutDates;
  setCheckInAndOutDates: (dates: CheckInAndOutDates) => void;
  checkInDateLimit?: moment.Moment;
  roundedSearchBar?: boolean;
};

const DatePicker = ({
  checkInAndOutDates,
  setCheckInAndOutDates,
  checkInDateLimit,
  roundedSearchBar,
}: Props) => {
  const { t } = useTranslation();
  const isAboveMD = useIsAboveBreakpoint('MD');
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [datePickerDate, setDatePickerDate] = useState<CheckInAndOutDates>(checkInAndOutDates);
  const dateLimit = checkInDateLimit || moment().add(12, 'months');

  // update check in and out dates if selecting date is actually finish to avoid unnecessary api request
  const updateCheckInAndOutDates = ({ checkInDate, checkOutDate }: CheckInAndOutDates) => {
    setDatePickerDate({ checkInDate, checkOutDate });
    setCheckInAndOutDates({ checkInDate, checkOutDate });
  };

  useEffect(() => {
    setDatePickerDate(checkInAndOutDates);
  }, [checkInAndOutDates, setDatePickerDate]);

  useEffect(() => {
    if (isAboveMD) {
      setIsModalOpen(false);
    }
  }, [isAboveMD, setIsModalOpen]);

  const events = {
    onMobileFocusChange: (input: FocusedInputShape | null) => {
      setFocusedInput(!input ? 'startDate' : input);
    },
    onConfirm: () => {
      const checkOutDate =
        datePickerDate.checkOutDate || datePickerDate.checkInDate?.clone().add(1, 'days') || null;

      updateCheckInAndOutDates({ ...datePickerDate, checkOutDate });
      setIsModalOpen(false);
    },
    onToggleModal: () => {
      setIsModalOpen(true);
      setFocusedInput('startDate');
    },
  };

  const Footer = useCallback(
    () => (
      <div
        className={classNames(
          'flex items-center py-3 px-[30px] border-t-[1px] border-gray-200 border-solid footer translate',
          {
            'border-transparent': isAboveMD && roundedSearchBar,
            'pt-0 pb-6': isAboveMD && roundedSearchBar,
            'justify-center': isAboveMD && roundedSearchBar,
            'justify-between': !isAboveMD,
          }
        )}
      >
        {errorMessage ? (
          <p className="footer-error-text text-small">{errorMessage}</p>
        ) : (
          <p className="footer-days-count-text text-small">
            <SkipTranslationText
              i18nKey="hotel:availability.date_picker.stay_nights"
              values={{
                stayNights:
                  datePickerDate.checkOutDate && datePickerDate.checkInDate
                    ? Math.ceil(
                        // hack to include a day which is less than 24 hours: https://github.com/moment/moment/issues/3713
                        datePickerDate.checkOutDate?.diff(datePickerDate.checkInDate, 'days', true)
                      )
                    : 0,
              }}
            />
          </p>
        )}
        {!isAboveMD && (
          <Button
            disabled={Boolean(errorMessage)}
            size="small"
            color="red"
            onClick={events.onConfirm}
          >
            {t('common:button.apply')}
          </Button>
        )}
      </div>
    ),
    [
      isAboveMD,
      roundedSearchBar,
      errorMessage,
      datePickerDate.checkOutDate,
      datePickerDate.checkInDate,
      t,
      events.onConfirm,
    ]
  );

  return (
    <>
      <div className="hidden md:block">
        <DateRangePicker
          readOnly
          startDatePlaceholderText={t('component:date_picker.placeholder.start_date')}
          endDatePlaceholderText={t('component:date_picker.placeholder.end_date')}
          startDate={datePickerDate.checkInDate}
          endDate={datePickerDate.checkOutDate}
          startDateId="start_date"
          endDateId="end_date"
          focusedInput={focusedInput}
          onFocusChange={(f) => setFocusedInput(f)}
          numberOfMonths={isAboveMD ? 2 : 1}
          withPortal={!isAboveMD}
          displayFormat={DISPLAY_DATE_FORMAT}
          showDefaultInputIcon
          hideKeyboardShortcutsPanel
          keepOpenOnDateSelect
          calendarInfoPosition="bottom"
          renderCalendarInfo={Footer}
          navPrev={<PrevButton />}
          navNext={<NextButton />}
          customInputIcon={
            roundedSearchBar ? (
              <Calender size={24} color={tailwindConfig.theme.colors.orange[700]} />
            ) : (
              <Time size={24} color={tailwindConfig.theme.colors.orange[700]} />
            )
          }
          customArrowIcon={
            <>
              <div className="line-top" />
              <Arrow size={14} color={tailwindConfig.theme.colors.orange[700]} />
              <div className="line-bottom" />
            </>
          }
          renderDayContents={(day) => (
            <p
              className={classNames(
                'flex justify-center items-center w-full h-full rounded-full day-contents',
                {
                  'font-bold': isAboveMD && roundedSearchBar,
                }
              )}
            >
              {day.format('D')}
            </p>
          )}
          onDatesChange={({ startDate, endDate }) => {
            if (startDate && endDate && focusedInput === 'startDate') {
              setErrorMessage('');
              setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
            } else if (endDate && Math.round(endDate.diff(startDate, 'days', true)) > 28) {
              // if stay nights selected are more than 28 nights, show an error message and keep it open
              setErrorMessage(t('component:date_picker.error.message'));
              setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
            } else if (startDate && endDate && focusedInput === 'endDate') {
              // if checkInDate and checkOutDate are both set, close it
              setErrorMessage('');
              setFocusedInput(null); // setting focusedInput null closes the date picker
              updateCheckInAndOutDates({ checkInDate: startDate, checkOutDate: endDate });
            } else {
              // else keep it open
              setErrorMessage('');
              setDatePickerDate({ checkInDate: startDate, checkOutDate: endDate });
            }
          }}
          onClose={() => {
            // if checkOutDate is null or undefined, set checkOutDate 1 day after the checkInDate
            const checkOutDate =
              datePickerDate.checkOutDate ||
              datePickerDate.checkInDate?.clone().add(1, 'days') ||
              null;

            updateCheckInAndOutDates({ ...datePickerDate, checkOutDate });
          }}
          isOutsideRange={(day) => {
            const isDateBeforeToday = !isInclusivelyAfterDay(day, moment()); // default react-dates condition
            const isDateAfterLimit = day.isAfter(dateLimit);

            return isDateBeforeToday || isDateAfterLimit;
          }}
        />
      </div>
      <div className="block md:hidden">
        <button
          className={classNames(
            'flex gap-4 items-center px-5 w-full h-12 bg-gray-50 rounded border border-gray-200 cursor-pointer',
            {
              'h-14 border-dashed bg-white border-x-0 hover:border-gray-200': roundedSearchBar,
              'hover:border-gray-300': !roundedSearchBar,
            }
          )}
          type="button"
          onClick={events.onToggleModal}
        >
          <div
            className={classNames('flex gap-4 items-center w-3/6', {
              'gap-2': roundedSearchBar,
            })}
          >
            {roundedSearchBar && (
              <Calender size={24} color={tailwindConfig.theme.colors.orange[700]} />
            )}
            <SkipTranslationWrapper tag="p" className="truncate">
              {datePickerDate.checkInDate?.format(DISPLAY_DATE_FORMAT)}
            </SkipTranslationWrapper>
          </div>
          <div className="relative">
            <div className="line-top" />
            <Arrow size={14} color={tailwindConfig.theme.colors.orange[700]} />
            <div className="line-bottom" />
          </div>
          <div className="flex w-3/6 items-center gap-4 overflow-x-hidden">
            <SkipTranslationWrapper tag="p" className="truncate">
              {datePickerDate.checkOutDate?.format(DISPLAY_DATE_FORMAT)}
            </SkipTranslationWrapper>
          </div>
        </button>
        {isModalOpen && (
          <Modal title={t('component:date_picker.modal.title')} onClose={events.onConfirm}>
            <div className="flex h-full flex-col">
              <div className="flex grow flex-col">
                <DayPickerRangeController
                  startDate={datePickerDate.checkInDate}
                  endDate={datePickerDate.checkOutDate}
                  initialVisibleMonth={() => moment()}
                  numberOfMonths={13}
                  onFocusChange={(f) => events.onMobileFocusChange(f)}
                  focusedInput={focusedInput}
                  orientation="verticalScrollable"
                  navPrev={<div />}
                  navNext={<div />}
                  hideKeyboardShortcutsPanel
                  renderDayContents={(day) => (
                    <p className="day-contents flex size-full items-center justify-center rounded-full">
                      {day.format('D')}
                    </p>
                  )}
                  onDatesChange={({ startDate, endDate }) => {
                    if (startDate && focusedInput === 'startDate') {
                      setErrorMessage('');
                      setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
                    } else if (endDate && Math.round(endDate.diff(startDate, 'days', true)) > 28) {
                      // if stay nights selected are more than 28 nights, show an error message and keep it open
                      setErrorMessage(t('component:date_picker.error.message'));
                      setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
                    } else if (startDate && endDate && focusedInput === 'endDate') {
                      // if checkInDate and checkOutDate are both set, close it
                      setErrorMessage('');
                      setFocusedInput(null); // setting focusedInput null closes the date picker
                      updateCheckInAndOutDates({ checkInDate: startDate, checkOutDate: endDate });
                      analytics.google.basic.track.select(
                        ContentType.DATE_PICKER,
                        `${startDate.toISOString()} - ${endDate.toISOString()}`
                      );
                    } else {
                      // else keep it open
                      setErrorMessage('');
                      setDatePickerDate({ checkInDate: startDate, checkOutDate: endDate });
                    }
                  }}
                  isOutsideRange={(day) => {
                    const isDateBeforeToday = !isInclusivelyAfterDay(day, moment()); // default react-dates condition
                    const isDateAfterLimit = day.isAfter(dateLimit);

                    return isDateBeforeToday || isDateAfterLimit;
                  }}
                />
              </div>
              {Footer()}
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

export default DatePicker;
